import { useEffect, useState } from 'react';
import { type Table } from '@tanstack/react-table';
import { Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface DataTableSearchProps<TData> {
  table: Table<TData>;
  placeholder?: string;
  className?: string;
}

export function DataTableSearch<TData>({
  table,
  placeholder = 'Search...',
  className,
}: DataTableSearchProps<TData>) {
  const [value, setValue] = useState((table.getState().globalFilter as string) ?? '');

  useEffect(() => {
    const timer = setTimeout(() => {
      table.setGlobalFilter(value);
    }, 300);
    return () => clearTimeout(timer);
  }, [value, table]);

  const globalFilter = table.getState().globalFilter as string;

  // Sync external changes
  useEffect(() => {
    if (globalFilter !== value) {
      setValue(globalFilter ?? '');
    }
  }, [globalFilter, value]);

  return (
    <div className={cn('relative', className)}>
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
      <Input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={placeholder}
        className="h-9 w-[250px] pl-9 pr-9 border-border rounded-md"
      />
      {value && (
        <Button
          variant="ghost"
          size="icon"
          aria-label="Dismiss"
          className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
          onClick={() => {
            setValue('');
            table.setGlobalFilter('');
          }}
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}
